import { ChangeDetectionStrategy, Component, DestroyRef, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { CrmConfig, SitecoreTokenCardDetailsConfig } from '@frontend/sports/common/client-config-data-access';
import { SportsProductService } from '@frontend/sports/host-app/sports-product/feature/sports-product-service';
import { fromSportsLeave } from '@frontend/sports/host-app/sports-product/feature/utils';
import { DialogAnimation, ModalDialogService, ModalRef } from '@frontend/sports/modal/feature';
import { RedirectHelperService } from '@frontend/sports/navigation/core/feature/redirect';
import { PickSourceProvider } from '@frontend/sports/option/pick/feature/tracking';
import { UserService } from '@frontend/sports/user/feature';
import { DeviceService } from '@frontend/vanilla/core';
import { firstValueFrom, merge } from 'rxjs';

import { BetAndGetToken, FreeBetToken, OddsBoostToken, RewardBannerTrackingConstants, RiskFreeToken } from '../../../reward-tokens.model';
import { RewardTokensBannerService, TokenCategories } from '../../../services/reward-tokens-banner.service';
import { TokenQuickViewDetailsComponent } from '../card-details/token-quick-view-details.component';
import { RewardTokenCardTrackingService } from './reward-token-card-tracking.service';

@Component({
    selector: 'bs-reward-token-card-info-link',
    templateUrl: 'reward-token-card-info-link.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: ['./reward-token-card-info-link.component.scss'],
    // eslint-disable-next-line @angular-eslint/prefer-standalone
    standalone: false,
})
export class RewardTokenCardInfoLinkComponent implements OnInit {
    @Input() token: BetAndGetToken | FreeBetToken | RiskFreeToken | OddsBoostToken;
    @Input() overrideLinkTitleHtml?: string;
    @Input() withArrow = true;
    @Input() tokenTitle?: string;
    @Input() tokenCategories: TokenCategories;
    @Input() promoHubUrlTokenType: string;
    @Output() betNowClicked = new EventEmitter<void>();

    tokenInfoUrl = '';
    tokenInfoLinkText = '';
    isRewardDrawerEnabled = false;
    isQuickViewEnabled = false;
    private modalHandler: ModalRef | null = null;
    private readonly destroyRef = inject(DestroyRef);

    constructor(
        private rewardTokensBannerService: RewardTokensBannerService,
        private pickSourceProvider: PickSourceProvider,
        private tokensConfig: CrmConfig,
        private dialogService: ModalDialogService,
        private deviceService: DeviceService,
        private sitecoreCardDetailsConfig: SitecoreTokenCardDetailsConfig,
        private redirectHelperService: RedirectHelperService,
        private userService: UserService,
        private rewardTokenCardTrackingService: RewardTokenCardTrackingService,
        private sportsService: SportsProductService,
    ) {}

    ngOnInit(): void {
        this.isRewardDrawerEnabled = this.tokensConfig.tokens.isRewardDrawerEnabled;
        this.isQuickViewEnabled = this.tokensConfig.tokens.isQuickViewEnabled;
        const tokenUrlDetails = this.rewardTokensBannerService.getTokenUrlDetails(this.token);
        this.tokenInfoUrl = tokenUrlDetails.tokenInfoUrl;
        this.tokenInfoLinkText = tokenUrlDetails.tokenInfoLinkText;

        const onSportsLeave = fromSportsLeave(this.sportsService);

        const onLogOut = this.userService.onUserLogout$;

        merge(onSportsLeave, onLogOut)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.closeModal();
            });
    }

    trackTokenInfoLinkClick(): void {
        this.rewardTokenCardTrackingService.trackCardInfoLinkClick(
            this.token.rewardTokenType,
            this.overrideLinkTitleHtml,
            this.tokenInfoUrl,
            this.pickSourceProvider.getTracking(),
        );
        if (this.isRewardDrawerEnabled && this.isQuickViewEnabled && this.token.customDescription) {
            this.showDetailsPopup();
        } else {
            this.redirectHelperService.goToPage(this.tokenInfoUrl);
            this.rewardTokensBannerService.trackBetRewardBanner(
                RewardBannerTrackingConstants.Click,
                this.pickSourceProvider.getTracking(),
                RewardBannerTrackingConstants.RewardDetails,
                this.token.rewardTokenType,
                this.tokenInfoUrl,
            );
        }
    }

    private async showDetailsPopup() {
        this.rewardTokenCardTrackingService.rewardTokenCardPopUpTrackingService(this.token.rewardTokenType);
        await firstValueFrom(this.sitecoreCardDetailsConfig.whenReady);
        this.closeModal();
        if (!this.modalHandler) {
            this.modalHandler = this.dialogService.openDialog(TokenQuickViewDetailsComponent, {
                data: {
                    token: this.token,
                    tokenInfoUrl: this.tokenInfoUrl,
                    tokenTitle: this.tokenTitle,
                    categories: this.rewardTokensBannerService.getTokenCategories(this.token!),
                    promoHubUrlTokenType: this.promoHubUrlTokenType,
                },
                settings: {
                    fit: false,
                    showPageHeader: false,
                    showDialogHeader: false,
                    isModalDialog: false,
                    animation: DialogAnimation.SlideInFromBottom,
                    closeAnimation: DialogAnimation.SlideOutFromTop,
                    cssClass: `${this.deviceService.isMobile ? 'reward-token-card-details-mobile' : ''} reward-token-card-details-quick-view`,
                    backdropClass: 'reward-token-card-details-modal-backdrop',
                },
            });
        }

        return this.modalHandler.result.then((data) => {
            if (data === 'betnow-clicked') {
                this.betNowClicked.emit();
            }
        });
    }

    private closeModal(): void {
        if (this.modalHandler) {
            this.modalHandler.close();
            this.modalHandler = null;
        }
    }
}
